<template>
  <div class="acc" :style="{ height: height + 'px' }">
    <div class="acc-toolbar">
      <span class="acc-readout" v-if="readout">
        <span class="acc-sym">{{ ticker }}</span>
        <span class="acc-dim">{{ intervalMinutes }}m · {{ readout.when }}</span>
        <span>O <b :class="readout.cls">{{ readout.o }}</b></span>
        <span>H <b :class="readout.cls">{{ readout.h }}</b></span>
        <span>L <b :class="readout.cls">{{ readout.l }}</b></span>
        <span>C <b :class="readout.cls">{{ readout.c }}</b></span>
        <span :class="readout.cls">{{ readout.chg }}</span>
        <span>V <b>{{ readout.v }}</b></span>
        <span v-for="m in readout.mas" :key="m.slot" :style="{ color: m.color }">{{ m.label }} {{ m.value }}</span>
      </span>
      <span class="acc-toggles">
        <label><input type="checkbox" v-model="showBands" /> zones</label>
        <label><input type="checkbox" v-model="showMinor" /> R1 R2 S1 S2</label>
        <label v-for="(a, i) in averageSeries" :key="'ma' + i" :style="{ color: a.color }"
               :title="`Show ${a.label}; the period box reloads the chart`">
          <input type="checkbox" v-model="averageVisible[i]" @change="saveAverageVisible" />
          {{ a.kind.toUpperCase() }}
          <input type="number" class="acc-period" min="1" :max="MAX_AVERAGE_PERIOD" step="1"
                 :value="a.period" @change="setPeriod(i, $event)" @keyup.enter="$event.target.blur()" />
        </label>
        <label><input type="checkbox" v-model="showVolume" /> volume</label>
        <label><input type="checkbox" v-model="showStochastic" /> stoch</label>
        <label><input type="checkbox" v-model="showMacd" /> MACD</label>
      </span>
    </div>
    <div ref="canvas" class="acc-canvas" @mouseenter="pointerIn = 'main'" @mouseleave="leave('main')"></div>
    <div v-for="p in PANES" v-show="paneShown(p.key)" :key="p.key" class="acc-pane"
         :style="{ height: PANE_HEIGHT + 'px' }" @mouseenter="pointerIn = p.key" @mouseleave="leave(p.key)">
      <div :ref="'pane-' + p.key" class="acc-pane-canvas"></div>
      <div class="acc-pane-legend">
        <span class="acc-dim">{{ p.title }}</span>
        <span v-for="l in paneLegend(p.key)" :key="l.label" :style="{ color: l.color }">{{ l.label }} {{ l.value }}</span>
      </div>
    </div>
    <div v-if="!candles.length" class="acc-empty">No candles for these sessions.</div>
  </div>
</template>

<script>
/**
 * The post-mortem chart: one session or several of candles at the
 * technical-analysis width, each session's Camarilla ladder, and the trade's
 * own markers -- all from the backend, nothing re-derived here.
 *
 * - The ladder, its zones, the session rules and the level labels are painted
 *   per session by LadderPrimitive (utils/ladderPrimitive.js), under the
 *   candles: v4 has no fill-between primitive, and a line series does not
 *   break on whitespace, so a series per level would join one session's
 *   ladder to the next. R5 R4 R3 P S3 S4 S5 are drawn (R4/S4 and R3/S3
 *   heavier); R1 R2 S1 S2 are available behind a toggle. The price axis
 *   labels the levels of the last session in view.
 * - The trade: the arming bar and its setup, the resting limit as a dotted
 *   line until it filled, the fill, every stop move with what moved it (a
 *   stepped dashed line plus a marker), and the exit with its rule.
 * - Evidence and action are kept apart. A stop move is drawn where it was
 *   ADOPTED (its ``at_epoch``): the marker and the step in the stop line sit
 *   on that bar, because until then the old stop was the only protection. The
 *   bar whose geometry justified the move (its ``bar_ts``) gets its own grey circle
 *   marker, labelled with both times and the gap between them -- on FTNT
 *   2026-10-07 the breakeven trigger was the 10:20 bar and the move landed at
 *   11:10, and drawing the stop from 10:20 showed fifty minutes of protection
 *   that did not exist. The exit likewise sits on the bar it happened in; a
 *   pattern exit's deciding bar is its own evidence marker.
 * - Each session is laid out whole: every slot from its first bar to its last
 *   (the bounds from the chart endpoint, so a half day ends at 13:00), slots
 *   without a bar as whitespace (utils/sessionSkeleton.js). Bars keep a
 *   normal width however few exist, and the ladder spans the whole day.
 * - Times are shown in New York time: lightweight-charts formats epoch seconds
 *   as UTC, so every timestamp is shifted by its ET offset before plotting.
 * - The price scale covers whole sessions: every session the view touches,
 *   its candles and its R4–S4 band (and the trade's levels on its own
 *   session). Panning inside a session never rescales; crossing into another
 *   session widens the scale once, to fit both. The moving averages do not
 *   take part in it: an SMA200 far from the day's range would squash the
 *   candles, so it runs off the edge instead.
 * - Three moving averages of the close at the chart's width (SMA200, SMA50
 *   and EMA10 unless the `averages` prop says otherwise), from the chart
 *   endpoint (warmed up on earlier sessions, so full from the first bar),
 *   each behind its own toggle with its own period box -- a new period is
 *   emitted (`update:averages`) for the view to reload, since the warm-up
 *   lives on the server -- and each bar's volume as a histogram along the
 *   bottom on its own scale, behind a toggle.
 * - The slow stochastic (14, 3, 3) and MACD (12, 26, 9), from the chart
 *   endpoint, each in its own pane under the candles. lightweight-charts 4.2
 *   has no panes, so each is a chart of its own: fed the candle series' whole
 *   skeleton as whitespace, so its logical indexes are the candles' and the
 *   visible logical range is mirrored between them exactly; the crosshair is
 *   mirrored from whichever chart the pointer is over; every price scale has
 *   one minimum width so the plot areas line up; and only the bottom chart
 *   shows the time axis. Behind toggles; display only, no setup reads them.
 */
import { createChart, CrosshairMode, LineStyle, LineType, PriceScaleMode } from 'lightweight-charts';
import { LadderPrimitive } from '../utils/ladderPrimitive';
import { mergeSkeleton, sessionSlots } from '../utils/sessionSkeleton';

/** TradingView's light palette; the markers darkened to read on white. */
const THEME = {
  bg: '#ffffff', text: '#131722', border: '#d1d4dc', grid: '#e1e3e6', up: '#26a69a', down: '#ef5350',
  crosshair: '#9598a1', crosshairLabel: '#131722',
  stop: '#e65100', limit: '#434651', arming: '#ab47bc', fill: '#0288d1', exit: '#131722',
  evidence: '#9598a1',
};

/** Drawn by default, heavier R4/S4 and R3/S3, lighter R5/S5 and P. */
const MAJOR_LEVELS = [
  { key: 'r5', label: 'R5', color: 'rgba(38, 166, 154, 0.7)', axisColor: '#1f7a72', width: 1 },
  { key: 'r4', label: 'R4', color: '#26a69a', width: 2 },
  { key: 'r3', label: 'R3', color: '#ef5350', width: 2 },
  { key: 'p', label: 'P', color: 'rgba(178, 181, 190, 0.7)', axisColor: '#5d606b', width: 1 },
  { key: 's3', label: 'S3', color: '#26a69a', width: 2 },
  { key: 's4', label: 'S4', color: '#ef5350', width: 2 },
  { key: 's5', label: 'S5', color: 'rgba(239, 83, 80, 0.7)', axisColor: '#a33b39', width: 1 },
];
/** The moving averages drawn when the view names none: one slot each, in drawing order. */
export const DEFAULT_AVERAGES = [
  { kind: 'sma', period: 200 },
  { kind: 'sma', period: 50 },
  { kind: 'ema', period: 10 },
];
/** Each slot's line style, by position. */
const AVERAGE_STYLES = [
  { color: '#2962ff', width: 2 },
  { color: '#ff9800', width: 2 },
  { color: '#e91e63', width: 1 },
];
/** The chart endpoint's longest period (MAX_AVERAGE_PERIOD in post_mortem_chart.py). */
export const MAX_AVERAGE_PERIOD = 500;
const VISIBLE_KEY = 'postMortem.averageVisible';
/** The oscillator panes under the candles, top to bottom. */
const PANES = [
  { key: 'stochastic', title: 'Stoch 14 3 3' },
  { key: 'macd', title: 'MACD 12 26 9' },
];
const PANE_HEIGHT = 120;
/** One width for every price scale, so the panes' plot areas line up with the candles'. */
const SCALE_WIDTH = 72;
const OSC = {
  k: '#2962ff', d: '#ff6d00', band: '#9598a1',
  macd: '#2962ff', signal: '#ff6d00',
  // TradingView's MACD histogram: strong when growing away from zero, pale when shrinking.
  histUpGrow: '#26a69a', histUpFall: '#b2dfdb', histDownGrow: '#ef5350', histDownFall: '#ffcdd2',
};
const VOLUME_UP = 'rgba(38, 166, 154, 0.45)';
const VOLUME_DOWN = 'rgba(239, 83, 80, 0.45)';

/** Available, off by default. */
const MINOR_LEVELS = [
  { key: 'r2', label: 'R2', color: 'rgba(178, 181, 190, 0.5)', axisColor: '#4a4e59', width: 1, dashed: true },
  { key: 'r1', label: 'R1', color: 'rgba(178, 181, 190, 0.5)', axisColor: '#4a4e59', width: 1, dashed: true },
  { key: 's1', label: 'S1', color: 'rgba(178, 181, 190, 0.5)', axisColor: '#4a4e59', width: 1, dashed: true },
  { key: 's2', label: 'S2', color: 'rgba(178, 181, 190, 0.5)', axisColor: '#4a4e59', width: 1, dashed: true },
];

const REASON_TEXT = {
  entry: 'entry',
  camarilla_breakeven: 'breakeven',
  camarilla_trail: 'ladder trail',
  camarilla_trail_target: 'trail target',
  camarilla_adverse_exit: 'adverse close',
  pattern_tightened_stop: 'pattern',
  entry_bar_tightened_stop: 'entry bar',
};

/** Seconds to add to an epoch to show its New York wall time on a UTC axis. */
function etOffset(epoch) {
  const d = new Date(epoch * 1000);
  const et = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const utc = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' }));
  return Math.round((et - utc) / 1000);
}
const toChart = (epoch) => epoch + etOffset(epoch);
const fmt = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—');
/** An epoch as New York HH:MM. */
const hhmm = (epoch) => new Date(toChart(Number(epoch)) * 1000).toISOString().slice(11, 16);
/** "+50m" between evidence and action. */
const gap = (from, to) => `+${Math.max(0, Math.round((Number(to) - Number(from)) / 60))}m`;
/** 1234567 -> "1.23M". */
const fmtVolume = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(Math.round(n));
};
const pretty = (s) => String(s || '').replace(/^camarilla_/, '').replace(/_/g, ' ').toUpperCase();

/** Which average slots are shown, remembered per viewer; all on when nothing is stored. */
function loadAverageVisible() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(VISIBLE_KEY));
    if (Array.isArray(stored)) return AVERAGE_STYLES.map((_s, i) => stored[i] !== false);
  } catch (e) { /* unreadable storage: show them all */ }
  return AVERAGE_STYLES.map(() => true);
}

export default {
  name: 'AnnotatedCandleChart',
  emits: ['update:averages'],
  props: {
    ticker: { type: String, default: '' },
    candles: { type: Array, default: () => [] },
    /** [{date, start_ts, end_ts}] from the chart endpoint. */
    sessions: { type: Array, default: () => [] },
    /** [{session_date, levels|null}] from /api/camarilla/levels. */
    ladders: { type: Array, default: () => [] },
    /** The trade block from the chart endpoint, or null. */
    trade: { type: Object, default: null },
    /** {<kind><period>: [{time, value}]} from the chart endpoint, e.g. {sma200, sma50, ema10}. */
    movingAverages: { type: Object, default: () => ({}) },
    /** [{kind: 'sma'|'ema', period}], one per slot: what the chart was asked for. */
    averages: { type: Array, default: () => DEFAULT_AVERAGES },
    /** {stochastic: {k, d}, macd: {macd, signal, histogram}}, each [{time, value}], from the chart endpoint. */
    oscillators: { type: Object, default: () => ({}) },
    intervalMinutes: { type: Number, default: 5 },
    height: { type: Number, default: 640 },
  },
  data() {
    return {
      showBands: true, showMinor: false, showVolume: true, averageVisible: loadAverageVisible(),
      showStochastic: true, showMacd: true, hover: null, pointerIn: null, PANES, PANE_HEIGHT,
      MAX_AVERAGE_PERIOD,
    };
  },
  computed: {
    bars() {
      return (this.candles || [])
        .filter((c) => c && Number.isFinite(c.time))
        .map((c) => ({ ...c, raw: c.time, time: toChart(c.time) }))
        .sort((a, b) => a.time - b.time);
    },
    /** Each average in chart time, and keyed by it for the readout. */
    averageSeries() {
      return AVERAGE_STYLES.map((style, slot) => {
        const spec = (this.averages || [])[slot] || DEFAULT_AVERAGES[slot];
        const key = `${spec.kind}${spec.period}`;
        const a = { ...style, slot, kind: spec.kind, period: spec.period, key,
                    label: `${spec.kind.toUpperCase()}${spec.period}` };
        const points = ((this.movingAverages || {})[key] || [])
          .filter((p) => p && Number.isFinite(p.time) && Number.isFinite(Number(p.value)))
          .map((p) => ({ time: toChart(p.time), value: Number(p.value) }))
          .sort((x, y) => x.time - y.time);
        return { ...a, points, byTime: new Map(points.map((p) => [p.time, p.value])) };
      });
    },
    /** Each oscillator line in chart time, keyed by time for the legends. */
    oscillatorSeries() {
      const prep = (points) => {
        const out = (points || [])
          .filter((p) => p && Number.isFinite(p.time) && Number.isFinite(Number(p.value)))
          .map((p) => ({ time: toChart(p.time), value: Number(p.value) }))
          .sort((x, y) => x.time - y.time);
        return { points: out, byTime: new Map(out.map((p) => [p.time, p.value])) };
      };
      const o = this.oscillators || {};
      const st = o.stochastic || {};
      const m = o.macd || {};
      return { k: prep(st.k), d: prep(st.d), macd: prep(m.macd), signal: prep(m.signal), histogram: prep(m.histogram) };
    },
    readout() {
      const bar = this.hover || this.bars[this.bars.length - 1];
      if (!bar) return null;
      const chg = bar.open ? ((bar.close - bar.open) / bar.open) * 100 : 0;
      const iso = new Date(bar.time * 1000).toISOString();
      return {
        when: `${iso.slice(0, 10)} ${iso.slice(11, 16)} ET`,
        o: fmt(bar.open), h: fmt(bar.high), l: fmt(bar.low), c: fmt(bar.close),
        chg: `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%`,
        v: fmtVolume((this.bars.find((b) => b.time === bar.time) || {}).volume),
        mas: this.averageSeries.filter((a) => this.averageVisible[a.slot])
          .map((a) => ({ slot: a.slot, label: a.label, color: a.color, value: fmt(a.byTime.get(bar.time)) })),
        cls: bar.close >= bar.open ? 'acc-up' : 'acc-down',
      };
    },
  },
  watch: {
    candles() { this.redraw(true); },
    ladders() { this.redraw(false); },
    trade() { this.redraw(false); },
    showBands() { this.redraw(false); },
    showMinor() { this.redraw(false); },
    movingAverages() { this.drawIndicators(); },
    averages() { this.drawIndicators(); },
    averageVisible: { deep: true, handler() { this.drawIndicators(); } },
    oscillators() { this.drawOscillators(); },
    showStochastic() { this.layout(); this.drawOscillators(); },
    showMacd() { this.layout(); this.drawOscillators(); },
    showVolume() { this.drawIndicators(); },
  },
  mounted() {
    this.build();
    this.redraw(true);
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    for (const p of Object.values(this.panes || {})) p.chart.remove();
    this.panes = {};
    if (this.chart) this.chart.remove();
    this.chart = null;
  },
  methods: {
    build() {
      const el = this.$refs.canvas;
      this.chart = createChart(el, {
        width: el.clientWidth,
        height: Math.max(200, this.height - 34),
        layout: { background: { color: THEME.bg }, textColor: THEME.text, fontSize: 12,
                  fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif' },
        grid: { vertLines: { visible: false }, horzLines: { visible: false } },
        crosshair: {
          mode: CrosshairMode.Normal,
          vertLine: { color: THEME.crosshair, labelBackgroundColor: THEME.crosshairLabel },
          horzLine: { color: THEME.crosshair, labelBackgroundColor: THEME.crosshairLabel },
        },
        // The bottom fifth is the volume histogram's (see drawIndicators).
        rightPriceScale: { borderColor: THEME.border, mode: PriceScaleMode.Normal, minimumWidth: SCALE_WIDTH,
                           scaleMargins: { top: 0.08, bottom: 0.22 } },
        timeScale: { borderColor: THEME.border, timeVisible: true, secondsVisible: false,
                     rightOffset: 4, barSpacing: 7, minBarSpacing: 2, fixLeftEdge: true },
        handleScale: { axisPressedMouseMove: { time: true, price: true } },
      });
      // Volume on its own overlay scale along the bottom, behind the candles.
      this.volumeSeries = this.chart.addHistogramSeries({
        priceScaleId: 'volume', priceFormat: { type: 'volume' }, priceLineVisible: false,
        lastValueVisible: false,
      });
      this.chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 }, visible: false });
      this.candleSeries = this.chart.addCandlestickSeries({
        upColor: THEME.up, downColor: THEME.down, borderUpColor: THEME.up, borderDownColor: THEME.down,
        wickUpColor: THEME.up, wickDownColor: THEME.down, priceLineVisible: false,
        // Whole sessions in view (see the header comment): no rescale while
        // panning inside a session.
        autoscaleInfoProvider: () => this.visibleRange(),
      });
      // Off the autoscale (see the header comment).
      this.averageLines = AVERAGE_STYLES.map((a) => this.chart.addLineSeries({
        color: a.color, lineWidth: a.width, priceLineVisible: false, lastValueVisible: false,
        crosshairMarkerVisible: false, autoscaleInfoProvider: () => null,
      }));
      this.primitive = new LadderPrimitive();
      this.candleSeries.attachPrimitive(this.primitive);
      this.limitSeries = this.chart.addLineSeries({
        color: THEME.limit, lineWidth: 2, lineStyle: LineStyle.Dotted, priceLineVisible: false,
        lastValueVisible: false, crosshairMarkerVisible: false, autoscaleInfoProvider: () => null,
      });
      this.stopSeries = this.chart.addLineSeries({
        color: THEME.stop, lineWidth: 2, lineStyle: LineStyle.Dashed, lineType: LineType.WithSteps,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        autoscaleInfoProvider: () => null,
      });
      // The axis labels follow the last session in view.
      this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
        const inView = this.sessionsInView();
        if (inView.length) this.primitive.setLabelSession(inView[inView.length - 1].date);
      });
      this.chart.subscribeCrosshairMove((param) => {
        if (this.pointerIn !== 'main') return;
        const d = param && param.seriesData ? param.seriesData.get(this.candleSeries) : null;
        this.hover = d && Number.isFinite(d.open) ? { ...d, time: param.time } : null;
        this.mirrorCrosshair('main', param && param.time);
      });
      this.buildPanes();
      this.syncRanges();
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart && el.clientWidth) this.chart.applyOptions({ width: el.clientWidth });
        for (const p of Object.values(this.panes)) {
          if (p.el.clientWidth) p.chart.applyOptions({ width: p.el.clientWidth });
        }
      });
      this.resizeObserver.observe(el);
      for (const p of Object.values(this.panes)) this.resizeObserver.observe(p.el);
      this.layout();
    },

    /** One chart per oscillator pane, its series and its guide lines. */
    buildPanes() {
      this.panes = {};
      for (const def of PANES) {
        const ref = this.$refs[`pane-${def.key}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        const chart = createChart(el, {
          width: el.clientWidth || this.$refs.canvas.clientWidth,
          height: PANE_HEIGHT,
          // The candle chart above carries the attribution; one per pane would sit on the lines.
          layout: { background: { color: THEME.bg }, textColor: THEME.text, fontSize: 11, attributionLogo: false,
                    fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif' },
          grid: { vertLines: { visible: false }, horzLines: { visible: false } },
          crosshair: {
            mode: CrosshairMode.Normal,
            vertLine: { color: THEME.crosshair, labelBackgroundColor: THEME.crosshairLabel },
            horzLine: { color: THEME.crosshair, labelBackgroundColor: THEME.crosshairLabel },
          },
          rightPriceScale: { borderColor: THEME.border, minimumWidth: SCALE_WIDTH,
                             scaleMargins: { top: 0.12, bottom: 0.08 } },
          timeScale: { borderColor: THEME.border, timeVisible: true, secondsVisible: false,
                       rightOffset: 4, barSpacing: 7, minBarSpacing: 2, fixLeftEdge: true },
          handleScale: { axisPressedMouseMove: { time: true, price: false } },
        });
        const line = (color, width, extra = {}) => chart.addLineSeries({
          color, lineWidth: width, priceLineVisible: false, lastValueVisible: false,
          crosshairMarkerVisible: false, ...extra,
        });
        const series = {};
        if (def.key === 'stochastic') {
          // 0-100 always: the 80 / 20 bands are what the reading is judged against.
          const fixed = () => ({ priceRange: { minValue: 0, maxValue: 100 } });
          series.k = line(OSC.k, 2, { autoscaleInfoProvider: fixed });
          series.d = line(OSC.d, 1, { autoscaleInfoProvider: fixed });
          for (const price of [80, 20]) {
            series.k.createPriceLine({ price, color: OSC.band, lineWidth: 1, lineStyle: LineStyle.Dashed,
                                       axisLabelVisible: true, title: '' });
          }
        } else {
          series.histogram = chart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
          series.histogram.createPriceLine({ price: 0, color: OSC.band, lineWidth: 1, lineStyle: LineStyle.Dotted,
                                             axisLabelVisible: false, title: '' });
          series.macd = line(OSC.macd, 2);
          series.signal = line(OSC.signal, 1);
        }
        chart.subscribeCrosshairMove((param) => {
          if (this.pointerIn !== def.key) return;
          const bar = param && param.time ? this.bars.find((b) => b.time === param.time) : null;
          this.hover = bar || null;
          this.mirrorCrosshair(def.key, param && param.time);
        });
        this.panes[def.key] = { chart, series, el };
      }
    },

    /** The candles' visible logical range, mirrored to and from every pane. */
    syncRanges() {
      const charts = () => [['main', this.chart], ...Object.entries(this.panes).map(([k, p]) => [k, p.chart])];
      let syncing = false;
      for (const [key, chart] of charts()) {
        chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
          if (syncing || !range) return;
          syncing = true;
          for (const [other, c] of charts()) if (other !== key) c.timeScale().setVisibleLogicalRange(range);
          syncing = false;
        });
      }
    },

    /** Put the crosshair of every chart but the one under the pointer at ``time`` (or clear it). */
    mirrorCrosshair(source, time) {
      const targets = [['main', this.chart, this.candleSeries, (t) => {
        const b = this.bars.find((x) => x.time === t);
        return b ? b.close : null;
      }]];
      for (const [key, p] of Object.entries(this.panes || {})) {
        const first = key === 'stochastic' ? 'k' : 'macd';
        targets.push([key, p.chart, p.series[first], (t) => this.oscillatorSeries[first].byTime.get(t)]);
      }
      for (const [key, chart, series, valueAt] of targets) {
        if (key === source) continue;
        const value = time != null ? valueAt(time) : null;
        if (value != null && Number.isFinite(value)) chart.setCrosshairPosition(value, time, series);
        else chart.clearCrosshairPosition();
      }
    },

    leave(key) {
      if (this.pointerIn !== key) return;
      this.pointerIn = null;
      this.hover = null;
      this.mirrorCrosshair(key, null);
    },

    paneShown(key) {
      return key === 'stochastic' ? this.showStochastic : this.showMacd;
    },

    /** The candle chart takes what the shown panes leave; only the bottom chart shows the time axis. */
    layout() {
      if (!this.chart) return;
      const shown = PANES.filter((p) => this.paneShown(p.key));
      this.chart.applyOptions({ height: Math.max(200, this.height - 34 - shown.length * PANE_HEIGHT) });
      this.chart.timeScale().applyOptions({ visible: !shown.length });
      const last = shown.length ? shown[shown.length - 1].key : null;
      for (const [key, p] of Object.entries(this.panes)) p.chart.timeScale().applyOptions({ visible: key === last });
      this.$nextTick(() => {
        for (const p of Object.values(this.panes)) {
          if (p.el.clientWidth) p.chart.applyOptions({ width: p.el.clientWidth });
        }
        const range = this.chart && this.chart.timeScale().getVisibleLogicalRange();
        if (range) for (const p of Object.values(this.panes)) p.chart.timeScale().setVisibleLogicalRange(range);
      });
    },

    /** The legend values at the hovered bar (or the last one). */
    paneLegend(key) {
      const bar = this.hover || this.bars[this.bars.length - 1];
      const o = this.oscillatorSeries;
      const at = (s) => fmt(bar ? o[s].byTime.get(bar.time) : null);
      if (key === 'stochastic') {
        return [{ label: '%K', value: at('k'), color: OSC.k }, { label: '%D', value: at('d'), color: OSC.d }];
      }
      return [{ label: 'MACD', value: at('macd'), color: OSC.macd },
              { label: 'signal', value: at('signal'), color: OSC.signal },
              { label: 'hist', value: at('histogram'), color: OSC.band }];
    },

    /**
     * Every pane gets the candle series' whole skeleton -- whitespace where a
     * line has no value -- so its logical indexes are the candles' own.
     */
    drawOscillators() {
      if (!this.panes) return;
      const times = (this.points || []).map((p) => p.time);
      const fill = (series, color) => times.map((time) => {
        const value = series.byTime.get(time);
        if (value == null) return { time };
        return color ? { time, value, color: color(time, value) } : { time, value };
      });
      const o = this.oscillatorSeries;
      const st = this.panes.stochastic;
      const empty = times.map((time) => ({ time }));
      st.series.k.setData(this.showStochastic ? fill(o.k) : empty);
      st.series.d.setData(this.showStochastic ? fill(o.d) : empty);
      const m = this.panes.macd;
      const hist = o.histogram.points;
      const prev = new Map(hist.map((p, i) => [p.time, i ? hist[i - 1].value : p.value]));
      m.series.histogram.setData(this.showMacd ? fill(o.histogram, (time, value) => {
        const growing = Math.abs(value) >= Math.abs(prev.get(time));
        if (value >= 0) return growing ? OSC.histUpGrow : OSC.histUpFall;
        return growing ? OSC.histDownGrow : OSC.histDownFall;
      }) : empty);
      m.series.macd.setData(this.showMacd ? fill(o.macd) : empty);
      m.series.signal.setData(this.showMacd ? fill(o.signal) : empty);
      const range = this.chart && this.chart.timeScale().getVisibleLogicalRange();
      if (range) for (const p of Object.values(this.panes)) p.chart.timeScale().setVisibleLogicalRange(range);
    },

    /**
     * The loaded bars grouped by session, in chart time, each with its full
     * skeleton: `slots` from the session's first bar to its last, and
     * `firstTime` / `lastTime` its ends.
     */
    sessionGroups() {
      const ladderBy = new Map((this.ladders || []).map((l) => [l.session_date, l.levels]));
      return (this.sessions || []).map((s) => {
        const bars = this.bars.filter((b) => b.raw >= s.start_ts && b.raw < s.end_ts);
        let slots = sessionSlots(s, this.intervalMinutes).map(toChart);
        // A payload without the bar bounds: the real bars alone, as before.
        if (!slots.length) slots = bars.map((b) => b.time);
        const firstTime = Math.min(slots[0], bars.length ? bars[0].time : Infinity);
        const lastTime = Math.max(slots[slots.length - 1], bars.length ? bars[bars.length - 1].time : -Infinity);
        return { date: s.date, bars, slots, firstTime, lastTime, levels: ladderBy.get(s.date) || null };
      }).filter((g) => g.bars.length);
    },

    /** The candle containing an epoch instant (raw seconds), or null. */
    barAt(epoch) {
      if (!Number.isFinite(Number(epoch))) return null;
      let found = null;
      for (const b of this.bars) { if (b.raw <= epoch) found = b; else break; }
      return found;
    },

    redraw(fit) {
      if (!this.chart) return;
      const groups = this.sessionGroups();
      this.groups = groups;
      // Every session's skeleton with its bars in it: whitespace holds the
      // empty slots on the axis and carries no price.
      this.points = mergeSkeleton(groups.flatMap((g) => g.slots), this.bars);
      this.candleSeries.setData(this.points);
      const drawn = [...MAJOR_LEVELS, ...(this.showMinor ? MINOR_LEVELS : [])];
      this.primitive.setData(
        groups.map((g) => ({ date: g.date, firstTime: g.firstTime, lastTime: g.lastTime, levels: g.levels })),
        drawn, this.showBands,
      );
      this.drawTrade();
      this.drawIndicators();
      this.drawOscillators();
      if (fit && this.bars.length) this.fitToTrade();
    },

    /** A new period for one slot: emitted for the view to reload; anything not 1..max is put back. */
    setPeriod(slot, event) {
      const period = Number(event.target.value);
      const current = this.averageSeries[slot];
      if (!Number.isInteger(period) || period < 1 || period > MAX_AVERAGE_PERIOD) {
        event.target.value = current.period;
        return;
      }
      if (period === current.period) return;
      const next = this.averageSeries.map((a) => ({ kind: a.kind, period: a.period }));
      next[slot] = { kind: current.kind, period };
      this.$emit('update:averages', next);
    },

    saveAverageVisible() {
      try { window.localStorage.setItem(VISIBLE_KEY, JSON.stringify(this.averageVisible)); } catch (e) { /* per-viewer nicety */ }
    },

    /** The moving averages and the volume histogram, each behind its toggle. */
    drawIndicators() {
      if (!this.chart) return;
      this.averageSeries.forEach((a, i) => this.averageLines[i].setData(this.averageVisible[i] ? a.points : []));
      this.volumeSeries.setData(this.showVolume
        ? this.bars.filter((b) => Number.isFinite(Number(b.volume))).map((b) => ({
          time: b.time, value: Number(b.volume), color: b.close >= b.open ? VOLUME_UP : VOLUME_DOWN,
        }))
        : []);
    },

    /** The sessions the visible logical range touches (whole sessions). */
    sessionsInView() {
      const groups = this.groups || [];
      const range = this.chart && this.chart.timeScale().getVisibleLogicalRange();
      if (!range || !groups.length) return groups;
      // Logical indexes count the skeleton's slots, whitespace included.
      const points = this.points || [];
      const from = points[Math.min(points.length - 1, Math.max(0, Math.floor(range.from)))];
      const to = points[Math.min(points.length - 1, Math.max(0, Math.ceil(range.to)))];
      if (!from || !to) return groups;
      return groups.filter((g) => g.lastTime >= from.time && g.firstTime <= to.time);
    },

    /** The price range of the sessions in view: candles, R4–S4, the trade's levels. */
    visibleRange() {
      const inView = this.sessionsInView();
      if (!inView.length) return null;
      let lo = Infinity;
      let hi = -Infinity;
      for (const g of inView) {
        for (const b of g.bars) { lo = Math.min(lo, b.low); hi = Math.max(hi, b.high); }
        if (g.levels) { lo = Math.min(lo, Number(g.levels.s4)); hi = Math.max(hi, Number(g.levels.r4)); }
      }
      const t = this.trade || {};
      if (inView.some((g) => g.date === t.session_date)) {
        for (const v of [t.planned_entry, t.stop, t.target]) {
          if (Number(v) > 0) { lo = Math.min(lo, Number(v)); hi = Math.max(hi, Number(v)); }
        }
      }
      return Number.isFinite(lo) ? { priceRange: { minValue: lo, maxValue: hi } } : null;
    },

    drawTrade() {
      const t = this.trade;
      const markers = [];
      let limit = [];
      let stop = [];
      if (t) {
        const long = String(t.side).toUpperCase() !== 'SHORT';
        const armingBar = t.arming_bar_ts ? this.bars.find((b) => b.raw === t.arming_bar_ts) : null;
        const fillBar = this.barAt(t.fill && t.fill.time);
        // Where the exit happened; its deciding bar (a pattern exit's) is evidence, drawn apart below.
        const exitBar = this.barAt(t.exit && t.exit.time)
          || (t.exit && t.exit.bar_ts && this.bars.find((b) => b.raw === t.exit.bar_ts));
        if (armingBar) {
          markers.push({ time: armingBar.time, position: long ? 'belowBar' : 'aboveBar', color: THEME.arming,
                         shape: 'circle',
                         text: `armed · ${pretty(t.setup)}${t.entry_order_type === 'stop' ? ' · stop entry' : ''}` });
          // The resting entry (a limit, or a stop): placed once the arming bar closed, until it filled.
          const end = fillBar || this.barAt(this.sessionEnd(armingBar.raw));
          const start = this.bars.find((b) => b.raw > armingBar.raw);
          if (start && end && Number(t.planned_entry) > 0 && end.time >= start.time) {
            limit = [{ time: start.time, value: Number(t.planned_entry) }];
            if (end.time > start.time) limit.push({ time: end.time, value: Number(t.planned_entry) });
          }
        }
        if (fillBar) {
          markers.push({ time: fillBar.time, position: long ? 'belowBar' : 'aboveBar', color: THEME.fill,
                         shape: long ? 'arrowUp' : 'arrowDown',
                         text: `fill ${t.fill.qty ?? ''} @ ${fmt(t.fill.price)}` });
        }
        stop = this.stopLine(t, fillBar, exitBar);
        for (const h of (t.stop_history || []).filter((h) => h && h.reason !== 'entry')) {
          const why = REASON_TEXT[h.reason] || h.reason;
          const acted = this.adoptedBar(h);
          const evidence = this.evidenceBar(h);
          if (acted) {
            const trigger = evidence && evidence !== acted ? ` · trigger ${hhmm(h.bar_ts)}` : '';
            // A take-profit move (the ladder trail arming) and the adverse exit's
            // decision to close leave the stop where it was.
            let moved = Number(h.target) > 0 ? `target → ${fmt(h.target)}` : `stop → ${fmt(h.stop)}`;
            if (h.reason === 'camarilla_adverse_exit') {
              const r = Number.isFinite(Number(h.adverse_r)) ? `, ${Number(h.adverse_r).toFixed(2)}R` : '';
              moved = `close decided: bar closed ${fmt(h.close)}${r}`;
            }
            markers.push({ time: acted.time, position: long ? 'belowBar' : 'aboveBar', color: THEME.stop,
                           shape: 'square', text: `${moved} (${why}) at ${hhmm(acted.raw)}${trigger}` });
          }
          if (evidence && evidence !== acted) {
            const when = Number(h.at_epoch) > 0 ? `, moved ${hhmm(h.at_epoch)} (${gap(h.bar_ts, h.at_epoch)})` : '';
            markers.push({ time: evidence.time, position: long ? 'aboveBar' : 'belowBar', color: THEME.evidence,
                           shape: 'circle', text: `${why} trigger bar ${hhmm(h.bar_ts)}${when}` });
          }
        }
        if (exitBar && t.exit) {
          markers.push({ time: exitBar.time, position: long ? 'aboveBar' : 'belowBar', color: THEME.exit,
                         shape: long ? 'arrowDown' : 'arrowUp',
                         text: `exit ${t.exit.rule || ''} @ ${fmt(t.exit.price)}`.replace('  ', ' ') });
          const signal = t.exit.bar_ts && this.bars.find((b) => b.raw === t.exit.bar_ts);
          if (signal && signal !== exitBar) {
            markers.push({ time: signal.time, position: long ? 'aboveBar' : 'belowBar', color: THEME.evidence,
                           shape: 'circle', text: `exit signal bar ${hhmm(t.exit.bar_ts)}` });
          }
        }
      }
      this.limitSeries.setData(limit);
      // A stop that is not a rung (the S5 long's s5_stop, the R5 short's r5_stop)
      // has no ladder line to read it against, so its axis label names it.
      const synthetic = t && /_stop$/.test(String(t.stop_level || ''));
      this.stopSeries.applyOptions({ title: synthetic ? pretty(t.stop_level) : '', lastValueVisible: synthetic });
      this.stopSeries.setData(stop);
      this.candleSeries.setMarkers(markers.sort((a, b) => a.time - b.time));
    },

    sessionEnd(raw) {
      const s = (this.sessions || []).find((x) => raw >= x.start_ts && raw < x.end_ts);
      return s ? s.end_ts - 1 : raw;
    },

    /** The bar a stop move was adopted in (``at_epoch``); the trigger bar only when no time was recorded. */
    adoptedBar(h) {
      return (Number(h.at_epoch) > 0 && this.barAt(h.at_epoch)) || this.evidenceBar(h);
    },

    /** The bar whose geometry justified a stop move (``bar_ts``), or null. */
    evidenceBar(h) {
      return (h.bar_ts && this.bars.find((b) => b.raw === Number(h.bar_ts))) || null;
    },

    /**
     * The stop, stepped: every level held until the bar it was ADOPTED in, to
     * the exit. Never the bar that justified the move: until the move landed
     * the old level was the protection.
     */
    stopLine(t, fillBar, exitBar) {
      const history = (t.stop_history || []).filter((h) => h && Number(h.stop) > 0);
      if (!history.length || !fillBar) return [];
      const points = new Map();
      for (const h of history) {
        const bar = h.reason === 'entry' ? fillBar : this.adoptedBar(h);
        if (bar) points.set(Math.max(bar.time, fillBar.time), Number(h.stop));
      }
      const data = [...points.entries()].sort((a, b) => a[0] - b[0]).map(([time, value]) => ({ time, value }));
      const end = exitBar || this.barAt(this.sessionEnd(fillBar.raw));
      if (data.length && end && end.time > data[data.length - 1].time) {
        data.push({ time: end.time, value: data[data.length - 1].value });
      }
      return data;
    },

    /**
     * Open on the trade's session (or the last one), the whole session in
     * view: its skeleton, not just the bars that exist.
     */
    fitToTrade() {
      const groups = this.groups || this.sessionGroups();
      if (!groups.length) return;
      const tradeDate = this.trade && this.trade.session_date;
      const g = groups.find((x) => x.date === tradeDate) || groups[groups.length - 1];
      this.chart.timeScale().setVisibleRange({ from: g.firstTime, to: g.lastTime });
    },
  },
};
</script>

<style scoped>
.acc { position: relative; background: #ffffff; border: 1px solid #d1d4dc; border-radius: 6px; display: flex; flex-direction: column; overflow: hidden; }
.acc-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 1rem;
               padding: 6px 12px; height: 34px; color: #131722; font-size: 12px;
               border-bottom: 1px solid #e0e3eb; font-variant-numeric: tabular-nums; }
.acc-readout { display: flex; gap: 0.9rem; align-items: baseline; white-space: nowrap; overflow: hidden; }
.acc-sym { color: #131722; font-weight: 600; font-size: 13px; }
.acc-dim { color: #787b86; }
.acc-up { color: #26a69a; }
.acc-down { color: #ef5350; }
.acc-toggles { display: flex; gap: 1rem; white-space: nowrap; }
.acc-toggles label { display: flex; gap: 0.3rem; align-items: center; cursor: pointer; margin: 0; }
.acc-period { width: 3.6rem; height: 20px; padding: 0 2px; font-size: 12px; color: #131722;
              border: 1px solid #d1d4dc; border-radius: 3px; font-variant-numeric: tabular-nums; }
.acc-canvas { flex: 1; min-height: 0; position: relative; }
.acc-pane { position: relative; flex: none; border-top: 1px solid #e0e3eb; }
.acc-pane-canvas { position: absolute; inset: 0; }
.acc-pane-legend { position: absolute; top: 3px; left: 8px; z-index: 2; display: flex; gap: 0.8rem;
                   font-size: 11px; pointer-events: none; font-variant-numeric: tabular-nums;
                   background: rgba(255, 255, 255, 0.8); padding: 0 4px; border-radius: 3px; }
.acc-empty { position: absolute; margin: 4rem auto; width: 100%; text-align: center; color: #787b86; }
</style>
